import { useAtomValue } from 'jotai'
import React from 'react'
import * as R from 'remeda'

import { useSurveyIdSampleSizeMap } from '~/hooks/useSurveyIdSampleSizeMap'
import { exists, findByPath, pathOr } from '~/utils'

import { tabulationQueryDataAtom } from '../atoms'
import { type DataItem } from '../utils'

import { useCurrentSurveyGroup } from './useCurrentSurveyGroup'
import { useVisibleSurveys } from './useVisibleSurveys'

type QuestionsDataItem = DataItem & {
  questionId: string
  option: string
  surveyId: string
}

export const useQuestionsData = (): QuestionsDataItem[] => {
  const tabulationQueryData = useAtomValue(tabulationQueryDataAtom)
  const surveyGroup = useCurrentSurveyGroup()
  const surveys = useVisibleSurveys()
  const surveyIdSampleSizeMap = useSurveyIdSampleSizeMap()

  return React.useMemo(
    () =>
      surveyGroup.questions.flatMap((question) =>
        question.answerOptions.flatMap((answerOption) =>
          surveys.map<QuestionsDataItem>((survey) => {
            const item = {
              questionId: question.id,
              option: answerOption.option,
              surveyId: survey.id,
              percentage: null,
              estimatedPopulation: null,
            }

            const count = R.pipe(
              tabulationQueryData?.questions ?? [],
              findByPath('surveyGroupQuestionId', question.id),
              pathOr('answers', []),
              findByPath('answerOption.option', answerOption.option),
              pathOr('counts', []),
              findByPath('surveyId', survey.id),
              pathOr('count', undefined),
            )

            if (!exists(count)) {
              return item
            }
            const percentage = count / (surveyIdSampleSizeMap[survey.id] ?? NaN)

            const numberOfTotalAnswers = survey.numberOfTotalAnswers ?? NaN
            const tamNumber = survey.metadata?.tamNumber ?? 0
            const tamRatio =
              tamNumber === 0 ? 1 : tamNumber / numberOfTotalAnswers

            const estimatedPopulation = Math.round((count * tamRatio) / 1000)

            return {
              ...item,
              percentage,
              estimatedPopulation,
            }
          }),
        ),
      ),
    [
      surveyGroup.questions,
      surveyIdSampleSizeMap,
      surveys,
      tabulationQueryData?.questions,
    ],
  )
}
